import {
	GetSettingParams,
	GetSettingsParams,
	SetSettingParams,
	SetSettingsParams,
	SystemInfo,
	InitializeParams,
} from '../Contracts/requests';
import {
	GetSettingResponse,
	GetSettingsResponse,
	SetSettingResponse,
	SetSettingsResponse,
	InitializeResponse,
} from '../Contracts/responses';
import {
	NativePlatform,
	NativePlatformType,
	SettingsEntry,
	SettingsEntryType,
} from '../Contracts/base';
import { AotCategoryType, logConstantsType } from '../utils/SettingsLogger';
import { NativeBridgeMapper } from './NativeBridgeMapper';

export abstract class SettingsNativeBridgeBase extends NativeBridgeMapper {
	protected _platform: NativePlatformType = NativePlatform.WINDOWS;
	protected _entrypoint: SettingsEntryType = SettingsEntry.ENDPOINT;
	protected _isInitialized = false;
	protected _systemInfo: SystemInfo | null = null;
	protected _onInitializedCallbacks: Array<(initializeParams: InitializeParams) => void> =
		[];
	protected _onRefreshSettingCallbacks: Array<(settingKey: string) => void> = [];

	//#region constructor
	constructor() {
		super();
	}

	/**
	 * Get the current platform
	 */
	getPlatform(): NativePlatformType {
		return this._platform;
	}

	/**
	 * Get the current entrypoint
	 */
	getEntrypoint(): SettingsEntryType {
		return this._entrypoint;
	}

	/**
	 * Check if the settings bridge has been initialized
	 */
	isInitialized(): boolean {
		return this._isInitialized;
	}

	/**
	 * Register a callback to be called when native requests a setting refresh
	 * @param callback - Function to call with the settingKey that needs refreshing
	 * @returns Cleanup function to unregister the callback
	 */
	onRefreshSetting(callback: (settingKey: string) => void): () => void {
		this._onRefreshSettingCallbacks.push(callback);
		return () => {
			const index = this._onRefreshSettingCallbacks.indexOf(callback);
			if (index > -1) {
				this._onRefreshSettingCallbacks.splice(index, 1);
			}
		};
	}

	/**
	 * Register a callback to be called when the bridge is initialized
	 * @param callback - Function to call when initialized
	 * @returns Cleanup function to unregister the callback
	 */
	onInitialized(callback: (initializeParams: InitializeParams) => void): () => void {
		this._onInitializedCallbacks.push(callback);

		// If already initialized, call immediately
		if (this._isInitialized && this._systemInfo) {
			callback({
				platform: this._platform,
				settingsEntrypoint: this._entrypoint,
				systemInfo: this._systemInfo,
			});
		}

		// Return cleanup function
		return () => {
			const index = this._onInitializedCallbacks.indexOf(callback);
			if (index > -1) {
				this._onInitializedCallbacks.splice(index, 1);
			}
		};
	}

	abstract writeTrace(
		level: logConstantsType,
		message: string,
		aotCategory?: AotCategoryType,
		aoTrace?: string
	): void;
	abstract refreshSetting(settingKey: string): void;
	abstract initialize(initializeParams: InitializeParams): Promise<InitializeResponse>;
	abstract settingsReady(): void;
	abstract onInitializeComplete(id: string, response: InitializeResponse): void;
	abstract getSetting(params: GetSettingParams): Promise<GetSettingResponse>;
	abstract getSettings(params: GetSettingsParams): Promise<GetSettingsResponse>;
	abstract setSetting(params: SetSettingParams): Promise<SetSettingResponse>;
	abstract setSettings(params: SetSettingsParams): Promise<SetSettingsResponse>;
	abstract onGetSettingComplete(id: string, response: GetSettingResponse): void;
	abstract onGetSettingsComplete(id: string, response: GetSettingsResponse): void;
	abstract onSetSettingComplete(id: string, response: SetSettingResponse): void;
	abstract onSetSettingsComplete(id: string, response: SetSettingsResponse): void;
}
